import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import FamilyHeader from "./FamilyHeader";
import { getSessionContext, hasFamilyClientSelection } from "../../common/utils/sessionContext";
import { persistFamilyClientSelection } from "../../common/utils/authSession";
import { getClientsByFamilyMember } from "../../common/api/familyportalOtpApi";
import * as Sentry from "@sentry/react";

const FamilySelectClient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { familyMemberId } = getSessionContext();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const selectClientAndGoHome = (client) => {
    persistFamilyClientSelection(client);
    navigate("/family-home", { replace: true });
  };

  useEffect(() => {
    if (hasFamilyClientSelection()) {
      navigate("/family-home", { replace: true });
      return undefined;
    }

    let cancelled = false;

    const loadClients = async (nextClients) => {
      if (cancelled) {
        return;
      }

      // Prefer an already-selected client (e.g. Flutter query params); otherwise default to first.
      if (nextClients.length >= 1) {
        const { familyClientId, familyMemberClientId } = getSessionContext();
        const preferredId = familyClientId || familyMemberClientId;
        const preferredClient =
          preferredId && nextClients.find((client) => String(client.clientId) === String(preferredId));
        selectClientAndGoHome(preferredClient || nextClients[0]);
        return;
      }

      setClients(nextClients);
      setLoading(false);
    };

    const fetchClients = async () => {
      setLoading(true);
      setError("");

      try {
        if (Array.isArray(location.state?.clients)) {
          await loadClients(location.state.clients);
          return;
        }

        const result = await getClientsByFamilyMember(familyMemberId);
        if (cancelled) {
          return;
        }

        if (!result.success || !Array.isArray(result.data)) {
          setError(result.message || "Failed to load clients.");
          setLoading(false);
          return;
        }

        await loadClients(result.data);
      } catch (error) {
        Sentry.captureException(error);
        if (!cancelled) {
          setError("Failed to load clients.");
          setLoading(false);
        }
      }
    };

    fetchClients();

    return () => {
      cancelled = true;
    };
  }, [familyMemberId, location.state?.clients, navigate]);

  const selectClient = (client) => {
    selectClientAndGoHome(client);
  };

  return (
    <div className="family-select-layout">
      <FamilyHeader variant="select" />
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>
      <div className={`family-select-page${!loading && clients.length > 0 ? " family-select-page--has-clients" : ""}`}>
        {!loading && !error && clients.length > 0 ? (
          <div className="family-select-intro">
            <p className="family-select-intro-line">You&apos;re here to stay connected and informed about your loved one&apos;s care.</p>
            <p className="family-select-intro-line">Select a client to view their information.</p>
          </div>
        ) : null}
        <div className="family-select-client-list">
          {!loading && error && <p className="family-select-error">{error}</p>}
          {!loading && !error && clients.length === 0 && (
            <p className="family-select-empty" role="status">
              No clients has been mapped to you. Please contact administrator
            </p>
          )}
          {!loading &&
            clients.map((client) => (
              <button key={client.clientId} type="button" className="family-select-client-btn" onClick={() => selectClient(client)}>
                {client.clientName}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default FamilySelectClient;
